import React, { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import useMutationWithConfirmation from '../../../../../../hooks/useMutationWithConfirmation';
import PaymentService from '../../../../../../services/PaymentService';
import calculateAmountUnapplied from '../helpers/calculateAmountUnapplied';
import formatCurrency from '../../../../../../helpers/formatCurrency';

const createDeleteDisabledMessage = (payment) => {
  if (!payment) {
    return '';
  }

  if (payment.status === 'applied') {
    return 'This payment cannot be deleted because it is applied.';
  }

  if (payment.status === 'void' && !!payment.billcom_receivedpay_id) {
    return 'This payment cannot be deleted because, even though it is void, it contains data about the original payment attempt.';
  }

  return '';
};

const createRefundDisabledMessage = (payment) => {
  if (!payment) {
    return '';
  }

  if (payment.status === 'void') {
    return 'This payment is void and cannot be refunded.';
  }

  // For ACH payments, check billcom_settlement
  if (
    payment.payment_method === 'ACH' &&
    (!payment?._billcom_settlement ||
      payment?._billcom_settlement?._billcom_settlement_parent !== 'Confirmed')
  ) {
    return 'This payment has not yet cleared.';
  }

  // For non-ACH payments, check if the check has cleared
  if (payment.payment_method !== 'ACH' && !payment.check_cleared) {
    return 'This payment has not yet cleared.';
  }

  if (!calculateAmountUnapplied(payment)) {
    return 'This payment has been fully applied so there is nothing to refund.';
  }

  return '';
};

export default function usePaymentActions({ payment, refetch, readOnly }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const amountUnapplied = calculateAmountUnapplied(payment);

  const cantBeDeleted =
    payment?.status === 'applied' ||
    (payment?.status === 'void' && !!payment?.billcom_receivedpay_id) ||
    readOnly;
  const cantBeRefunded =
    payment?.status === 'void' ||
    (payment?.payment_method === 'ACH' &&
      (!payment?._billcom_settlement ||
        payment?._billcom_settlement?._billcom_settlement_parent !==
          'Confirmed')) ||
    (payment?.payment_method !== 'ACH' && !payment?.check_cleared) ||
    !amountUnapplied ||
    readOnly;

  const { confirm: deletePayment, isPending: isDeletingPayment } =
    useMutationWithConfirmation({
      confirmationMessage: 'Are you sure you want to delete this payment?',
      successMessage: 'Payment deleted',
      mutationFn: () => PaymentService.deletePayment(payment.id),
      refetch: () => {
        queryClient.invalidateQueries({
          queryKey: ['payments'],
        });
        navigate(`/loans/${payment.loan_id}?tab=Payments`);
      },
      confirmButtonLabel: 'Yes, delete it',
      cancelButtonLabel: 'No, cancel',
      dialogType: 'danger',
    });

  const { confirm: refundOverpayment, isPending: isRefunding } =
    useMutationWithConfirmation({
      confirmationMessage: `Are you sure you want to refund the remaining $${formatCurrency(
        amountUnapplied
      )} that us unapplied to the borrower?`,
      successMessage: 'Payment deleted',
      mutationFn: () => PaymentService.refundUnapplied(payment.id),
      refetch,
      confirmButtonLabel: 'Yes, refund',
      cancelButtonLabel: 'No, cancel',
      dialogType: 'danger',
    });

  const actions = useMemo(() => {
    if (!payment) {
      return [];
    }

    return [
      {
        actionTitle: 'Refund overpayment',
        actionDescription: cantBeRefunded
          ? createRefundDisabledMessage(payment)
          : 'The unapplied amount of this payment will be refunded to the borrower. If the borrower is setup for ACH, the refund will be sent via ACH. Otherwise, the refund will be sent via check.',
        onClick: refundOverpayment,
        disabled: cantBeRefunded || isRefunding,
      },
      {
        actionTitle: 'Delete payment',
        actionDescription: cantBeDeleted
          ? createDeleteDisabledMessage(payment)
          : 'Delete this payment.',
        disabled: cantBeDeleted || isDeletingPayment,
        icon: (
          <span className="text-red-500">
            <svg className="w-8 h-8 fill-current" viewBox="0 0 32 32">
              <path d="M13 15h2v6h-2zM17 15h2v6h-2z" />
              <path d="M20 9c0-.6-.4-1-1-1h-6c-.6 0-1 .4-1 1v2H8v2h1v10c0 .6.4 1 1 1h12c.6 0 1-.4 1-1V13h1v-2h-4V9zm-6 1h4v1h-4v-1zm7 3v9H11v-9h10z" />
            </svg>
          </span>
        ),
        onClick: deletePayment,
      },
    ];
  }, [
    cantBeDeleted,
    cantBeRefunded,
    deletePayment,
    isDeletingPayment,
    isRefunding,
    payment,
    refundOverpayment,
  ]);

  return { actions, isDeletingPayment };
}
